import { constVoid } from 'fp-ts/function';
import { z } from 'zod';
import { useState } from 'react';
import { AsideDialog, OptimusProvider, UnsavedChangesDialog } from '@mews-commander/ui';
import {
    LocalizedText,
    createZodFormResolver,
    useLocalizeProperty,
    useFormControls,
    Validation,
} from '@mews/framework';
import { TextKey, DataTestId } from '@mews-commander/enums';
import { Banner, Button, DialogProps, Stack } from '@optimus-web/core';
import { MAX_SMALL_INPUT_TEXT_LENGTH } from '@mews-commander/constants';
import { RateGroup } from '../../../tables/RateGroups/types';
import { checkIfCentralRateGroup } from '../../../tables/RateGroups/utils';
import { GeneralDetailsMetaFields } from './fields/GeneralDetailsMetaFields';
import { FIELD_NAMES, GeneralDetailsMetaFormValues } from './formEnums';
import { useResetIfInactive } from './fields/hooks';

const reqularValidationSchema = z.object({
    [FIELD_NAMES.NAME]: Validation.String.Required.max(MAX_SMALL_INPUT_TEXT_LENGTH),
    [FIELD_NAMES.SHORT_NAME]: z.string().max(MAX_SMALL_INPUT_TEXT_LENGTH).nullish(),
    [FIELD_NAMES.DESCRIPTION]: z.string().nullish(),
    [FIELD_NAMES.ORDERING]: z.number().nonnegative(),
    [FIELD_NAMES.RATE_GROUP_KEY]: Validation.String.Required.max(MAX_SMALL_INPUT_TEXT_LENGTH),
});

const nonLinkedValidationSchema = reqularValidationSchema.extend({
    [FIELD_NAMES.RATE_GROUP_KEY]: z.string().max(MAX_SMALL_INPUT_TEXT_LENGTH),
});

interface Props {
    rateGroup: RateGroup;
    isLoading: boolean;
    isOpen: boolean;
    linkedRateGroups: number;
    isPortfolioManaged?: boolean;
    onSubmit: (values: GeneralDetailsMetaFormValues) => void;
    onClose: () => void;
}

export const EditGeneralDetailsDialog = ({
    isLoading,
    isOpen,
    rateGroup,
    linkedRateGroups,
    isPortfolioManaged,
    onSubmit,
    onClose,
}: Props) => {
    const [unsaved, setUnsaved] = useState(false);
    const localizeProperty = useLocalizeProperty();
    const isCentralRateGroup = checkIfCentralRateGroup(rateGroup);
    const validationSchema =
        isCentralRateGroup || isPortfolioManaged ? reqularValidationSchema : nonLinkedValidationSchema;

    type EditFormValues = z.infer<typeof validationSchema>;

    const resolver = createZodFormResolver(validationSchema);

    const { renderFormContent, submit, hasDirtyFields, reset } = useFormControls<EditFormValues>({
        onSubmit,
        resolver,
        values: {
            name: rateGroup.name,
            ordering: rateGroup.ordering,
            description: isCentralRateGroup ? rateGroup.description : localizeProperty(rateGroup?.descriptions),
            shortName: isCentralRateGroup ? rateGroup.shortName : localizeProperty(rateGroup?.shortNames),
            rateGroupKey: (isCentralRateGroup ? rateGroup.rateGroupKey : rateGroup.externalIdentifier) || '',
        },
    });

    useResetIfInactive({
        active: isOpen,
        reset,
    });

    const handleDiscard = () => {
        setUnsaved(false);
        onClose();
    };

    const handleClose = () => {
        if (hasDirtyFields) {
            setUnsaved(true);
            return;
        }

        handleDiscard();
    };

    const actions: DialogProps['actions'] = [
        {
            children: <LocalizedText textKey={TextKey.Save} />,
            variant: 'primary',
            onClick: submit,
            type: 'submit',
            'data-test-id': DataTestId.CentralRateGroupEditButton,
        },
    ];

    return (
        <OptimusProvider density="spacious">
            <AsideDialog
                actions={actions}
                active={isOpen}
                loading={isLoading}
                onClose={isLoading ? constVoid : handleClose}
                title={<LocalizedText textKey={TextKey.RateGroupGeneralDetailsEditDialogTitle} />}
                footerDetail={
                    <Button variant="tertiary" onClick={onClose} type="submit">
                        <LocalizedText textKey={TextKey.Cancel} />
                    </Button>
                }
            >
                <Stack spacing="200" vertical>
                    {isCentralRateGroup ? (
                        <Banner
                            variant="info"
                            primaryText={
                                <LocalizedText
                                    textKey={TextKey.RateGroupGeneralDetailsEditBanner}
                                    substitutes={{
                                        count: linkedRateGroups,
                                    }}
                                />
                            }
                        />
                    ) : null}
                    {renderFormContent(
                        <Stack vertical distribution="space-between" spacing="200">
                            <GeneralDetailsMetaFields
                                isRateGroupKeyRequired={isCentralRateGroup || isPortfolioManaged}
                                isRateGroupKeyDisabled={isPortfolioManaged}
                            />
                        </Stack>
                    )}
                </Stack>
            </AsideDialog>
            {unsaved ? (
                <UnsavedChangesDialog onCancel={() => setUnsaved(false)} onDiscardChanges={handleDiscard} />
            ) : null}
        </OptimusProvider>
    );
};
